import {
  CrearTareaRequest,
  CrearTareaResponse,
  GestionLlamadaRequest,
  GestionLlamadaResponse,
  OrdenServicioDetalleHidratada,
  PrepararIngresoCumplimientoRequest
} from '../../model/sin-oc/sin-oc';

/**
 * Puerto Abstracto: SinOcRepository
 *
 * Define el contrato para operaciones de Sin Orden de Compra (Tarea).
 * Migrado desde: CumplimientoSinOcActivity.java (legacy GWT)
 *
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpSinOcRepository (adaptador) lo implementa en infraestructura
 *
 * El DNI del usuario lo resuelve el backend desde el contexto SSO; las
 * implementaciones no deben enviarlo en headers ni en el cuerpo.
 *
 * @author Sura - Migración GWT a Angular
 */
export abstract class SinOcRepository {

  /**
   * Crea una tarea sin Orden de Compra.
   *
   * Flujo en backend:
   * 1. Validar homologación asesor (FN_GET_SNTAREA_ASESOR)
   * 2. Crear beneficiario/CT (SP_CREAR_BENEF_CT)
   * 3. Crear OC + cumplimiento (SP_CREAR_OC_CUMPLIMIENTO_TAREA)
   * 4. SELECT DSORDEN/DSPLAN/DSVERSION/DSACTIVIDAD en TPSC_ORDEN_SERVICIO_DETAL
   *
   * Endpoint: POST /api/servicios/sin-oc/tarea
   *
   * @param request Datos del formulario
   * @returns Datos de la orden creada (dsOrden, dsPlan, dsVersion, dsActividad)
   *
   * @legacy CumplimientoSinOcActivity.ingresarCumplimientoTarea()
   */
  abstract crearTarea(request: CrearTareaRequest): Promise<CrearTareaResponse>;

  /**
   * Registra una gestión de llamada asociada a un beneficiario.
   *
   * Flujo en backend:
   * 1. Invoca SP_PSC_CREAR_BENEFICI_PERSONA (crea persona+beneficiario si no existen)
   * 2. Persiste registro en TPSC_GESTION_LLAMADAS
   *
   * Endpoint: POST /api/servicios/sin-oc/gestion
   *
   * @legacy CallsManagementHandler.saveOrUpdateCalls() → GestionLlamadaFacade.saveOrUpdate()
   */
  abstract ingresarGestion(request: GestionLlamadaRequest): Promise<GestionLlamadaResponse>;

  /**
   * Prepara el ingreso de un cumplimiento sin OC (HU1132420) desde el flujo
   * Sin Orientación de Servicio.
   *
   * Flujo en backend:
   * 1. Validar homologación asesor (FN_GET_SNTAREA_ASESOR)
   * 2. Crear/actualizar beneficiario y centro de trabajo (SP_CREAR_BENEF_CT)
   * 3. Hidratar la orden con NMCONSECUTIVO del asesor, DNI/nombre del
   *    beneficiario y descripciones de municipios
   *
   * NO guarda cumplimiento. La captura de fechas/cantidad y datos del
   * cumplimiento se hace en el FormularioCumplimientoComponent del frontend.
   *
   * Endpoint: POST /api/servicios/sin-oc/preparar-ingreso-cumplimiento
   *
   * @legacy CumplimientoSinOcActivity.ingresarCumplimiento(dto)
   */
  abstract prepararIngresoCumplimiento(
    request: PrepararIngresoCumplimientoRequest
  ): Promise<OrdenServicioDetalleHidratada>;
}
